import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import SideNavbar from "../components/common/sideNavbar/sideNavbar";
import "./layout.css";

const Layout = ({
  children,
  isPopup = false,
  isHideHeader,
  invalidPage = false,
}) => {
  const location = useLocation();
  // Page swaps are the most repeated motion in the app (every tab tap), so
  // they stay short and opacity-only: a transform here would become the
  // containing block for every fixed footer and header on the page and drag
  // it along. With mode="wait" the old page must finish leaving before the
  // new one starts, so the exit is kept quicker than the entrance to keep the
  // blank beat between them short. Ease-out both ways.
  //
  // The entrance is the CSS `.page-enter` animation (layout.css); Framer only
  // runs the exit. Framer 12 hands opacity to the Web Animations API and only
  // writes the end value after that animation is gone, so for one frame the
  // page showed its start value: it blinked out after fading in, and blinked
  // back in after fading out. The `onUpdate` below is there to opt the exit
  // out of that path (Framer cannot report per-frame values from WAAPI, so it
  // animates on the main thread instead, writing the style every frame); it
  // lasts 100ms, so running it there costs nothing.
  const ease = [0.23, 1, 0.32, 1];
  const transition = {
    initial: false,
    className: "page-enter",
    exit: { opacity: 0, transition: { duration: 0.1, ease } },
    onUpdate: () => {},
  };
  // Reset scroll in the gap between pages, once the old one has gone and
  // before the new one paints, so the jump is never seen. Without it a page
  // opened from a scrolled one arrived already scrolled.
  const resetScroll = () => window.scrollTo(0, 0);
  return (
    <div className="overflow-x-hidden">
      {!isHideHeader && (
        <SideNavbar isPopup={isPopup} invalidPage={invalidPage} />
      )}
      {!isPopup ? (
        /* The side rail is fixed-position, so the page has to be inset by its
           width. That inset is `--sidenav-width`, published by SideNavbar from
           the rail's measured width (0 when there is no rail, e.g. on a phone),
           rather than the breakpoint guesses this used to carry — those keyed
           off the viewport while the rail's width keys off whether it is
           collapsed, so the two disagreed on every iPad and in every RTL
           layout. `padding-inline-start` also removes the need for a separate
           RTL rule: it follows the reading direction on its own. */
        <main
          style={{ paddingInlineStart: "var(--sidenav-width, 0px)" }}
          /* No transition here on purpose: the rail already animates its own
             width, the observer republishes on every frame of that animation,
             and a second easing on top of it would trail behind the rail. */
          className="sm:pt-12 max767:pb-[100px] overflow-x-hidden"
        >
          <div className=" policyOverviewDiv ipad:p-0 max767:p-0 max767:mt-[0px] overflow-x-hidden">
            <AnimatePresence mode="wait" onExitComplete={resetScroll}>
              <motion.div key={location.pathname} {...transition}>
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      ) : (
        <main className="sm:pt-12 overflow-x-hidden">
          <AnimatePresence mode="wait" onExitComplete={resetScroll}>
            <motion.div key={location.pathname} {...transition}>
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      )}
    </div>
  );
};

export default Layout;
